import type { Plugin, State, System } from "@dylanebert/shallot";
import {
    Color,
    f32,
    Inputs,
    LinesPlugin,
    Orbit,
    OrbitSmooth,
    Part,
    PartPlugin,
    segment,
    sparse,
    Text,
    text,
    TextPlugin,
    Transform,
} from "@dylanebert/shallot";
import { start } from "./boot";

// The harmony demo: one shallot scene that grows from a single GPU lane up to MAX_LANES, driven
// entirely by data. `config` is the single source of truth; systems reconcile geometry, blocks, and
// camera to it every frame. Mutate it live from the console via `__demo` (e.g. `__demo.lanes = 4`).
// Educational sequencing layers on top later by adjusting `config` — it never touches systems.
const config = {
    lanes: 1,
    batch: 1,
};

const MAX_LANES = 4; // by convention: past 4 the lanes get too small/busy under this fixed framing

// clamp the raw config values to usable integer counts. config stays the declarative data; everything
// derives counts through these so garbage input (floats, out of range) is tolerated. batch is the prompt
// pool size and can grow arbitrarily (the queue runs off-screen), so it has no upper clamp.
const laneCount = () => Math.max(1, Math.min(MAX_LANES, Math.round(config.lanes)));
const batchCount = () => Math.max(0, Math.round(config.batch));

const GROUND_Y = 0;
const LANE_WIDTH = 1.3; // z-extent of one lane; lanes are contiguous, so this is also rail spacing
const HALF_LENGTH = 100; // ground rails run far past the frustum
const RAIL_COLOR = 0x28282a; // match the blog text black
const RAIL_THICKNESS = 2;
const BARRIER_THICK = 3.5; // vertical sync line drawn across all lanes at a train/optim barrier
const BARRIER_LABEL_SIZE = 0.3; // "train"/"optim" text below each sync line, labeling it
const BARRIER_LABEL_DZ = 0.45; // gap past the stack's far rail where the label sits (below the line)
const BARRIER_LABEL_Y = GROUND_Y + 0.02; // flat on the ground, like the GPU/Prompts labels

// housing: a compact gantry at the left end where the labels live. wall dividers slot the lanes, a
// ceiling caps it, the GPU labels ride on top. blocks scroll left into its mouth and vanish under it.
const HOUSING_RIGHT = -6.5; // right mouth of the gantry, ahead of the now line
const HOUSING_DEPTH = 3; // extent along X (time axis); kept narrow so it stays in the fixed frame
const HOUSING_X = HOUSING_RIGHT - HOUSING_DEPTH / 2;
const HOUSING_COLOR: [number, number, number] = [0.62, 0.62, 0.62]; // 0x9e9e9e, offset off the white ground
const WALL_HEIGHT = 0.9;
const WALL_THICKNESS = 0.06;
const CEILING_THICKNESS = 0.08;
const CEILING_Y = WALL_HEIGHT + CEILING_THICKNESS / 2;

const LABEL_COLOR = 0x28282a;
const LABEL_Y = WALL_HEIGHT + CEILING_THICKNESS + 0.01; // just above the ceiling's top face
// per cell: the GPU label sits top-left, the state status label bottom-right, both on the housing ceiling.
const GPU_LABEL_SIZE = 0.32;
const STATE_LABEL_SIZE = 0.3;
const GPU_LABEL_DX = -0.6; // left of cell centre (toward the back of the housing)
const GPU_LABEL_DZ = 0.33; // in from the cell's top edge railZ(i)
const STATE_LABEL_DX = 0.55; // right of cell centre (toward the mouth)
const STATE_LABEL_DZ = -0.33; // in from the cell's bottom edge railZ(i + 1)

// conveyor: belt scroll speed (world units/sec) when moving, and a block's footprint within its lane.
const SPEED = 1.6;
const BLOCK_HEIGHT = 0.7;
const BLOCK_WIDTH = 0.55; // z-extent (across the lane)
const PITCH = 0.8; // belt-coord spacing between consecutive blocks in a rollout
const BLOCK_GAP = 0.06; // slight gap off each block's pitch so neighbours read as separate tokens
const BLOCK_LEN = PITCH - BLOCK_GAP; // x-extent of the drawn block
const BASE_Y = GROUND_Y + BLOCK_HEIGHT / 2; // resting height of a block centre on the ground plane

// block kinds and their colors. a rollout opens with one prompt block, then rollout (generated) tokens;
// the train and optimize kinds are emitted by the training and optimizer phases after the barriers.
const KIND = { prompt: 0, rollout: 1, train: 2, optimize: 3 } as const;
// seaborn "colorblind" palette: cohesive, and saturated enough to read as label text on the light-grey
// housing as well as solid blocks on the white belt. prompt shares the rollout hue, one shade stronger.
const rgb = (hex: number): [number, number, number] => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
const C_ROLLOUT = 0x0173b2; // blue
const C_PROMPT = 0x014e79; // darker blue (same hue, stronger) — opens each rollout
const C_TRAIN = 0x28a948; // green (product Green/700; reads clearly green, not teal)
const C_OPTIM = 0xd55e00; // burnt orange
const KIND_COLOR: Record<number, [number, number, number]> = {
    [KIND.prompt]: rgb(C_PROMPT),
    [KIND.rollout]: rgb(C_ROLLOUT),
    [KIND.train]: rgb(C_TRAIN),
    [KIND.optimize]: rgb(C_OPTIM),
};

// prompt pool: the dataset of waiting prompts, as a FIFO queue above the lanes. it holds `batch` prompt
// cubes in a single column centered over the lanes (X), with the front slot nearest the belt and the queue
// running off-screen in −Z as batch grows. each prompt continuously eases toward its slot, so when the front
// is pulled the rest advance forward. the "Prompts" label sits to the left of the front (the column grows
// away from it). a pulled prompt is entity-swapped into a belt Block in place — no separate transit entity.
const POOL_X = -2.5; // centered over the lanes in X (matches the camera's left pan)
const POOL_FRONT_Z = -2.0; // front slot (q = 0), just above GPU 0; higher q runs toward −Z, off-screen
const POOL_PITCH_Z = 0.82; // slot spacing along Z; > BLOCK_WIDTH so pooled cubes read as separate
const POOL_EASE_RATE = 8; // continuous-ease rate (1/s) toward the slot — frame-rate independent
const POOL_STAGGER = 1.3; // a new back-of-queue prompt spawns this far behind its slot, then eases in
const POOL_LABEL_DX = 1.5; // "Prompts" label offset to the left of the front slot
const POOL_RAIL_PAD = 0.13; // gap between the prompt cubes and the queue's side rails
const poolSlotZ = (q: number) => POOL_FRONT_Z - q * POOL_PITCH_Z;

// entrances. each block eases from its spawn offset straight to its belt resting position (easeOutExpo):
// rollout tokens drop in from above, the prompt flies in from wherever it sat in the pool when pulled.
const DROP_HEIGHT = 2.5;
const DROP_DURATION = 0.3;
const FLIGHT_DURATION = 0.55;

// the loop. each lane (GPU) is an independent state machine sharing one belt and one prompt pool. a global
// tick advances the belt one slot per EMIT_INTERVAL; at each tick a lane emits the block its state calls for,
// or nothing (a gap) while it waits at a barrier. the belt starts still and begins scrolling once the leading
// edge crosses FILL_THRESHOLD, so the first slots drop in before the conveyor runs.
//
//   gen      generate a rollout: pull a prompt (purple) from the pool, then emit rollout tokens (red). when
//            the rollout ends, pull another if the pool has one; if the pool is empty, go to genDone.
//   genDone  finished generating, waiting for every lane (the training barrier). idle.
//   train    emit training blocks (yellow), count = max(TRAIN_MIN, ceil(blocks this lane generated /
//            TRAIN_DIVISOR)): the more a lane generated, the longer it trains — the post-sync straggler.
//   trainDone waiting for every lane (the optim barrier). idle.
//   optim    emit OPTIM_BLOCKS optimizer blocks (orange), short and uniform, as one block spanning all lanes.
//   optimDone done. once all lanes are here and the belt has drained, the pool refills and the loop restarts.
const EMIT_INTERVAL = PITCH / SPEED; // tick cadence; == belt speed so the leading edge holds steady
const BELT_START_U = HOUSING_RIGHT + 0.5; // belt-coord of the first slot, just past the housing mouth
const BELT_RIGHT = 6; // right working edge of the visible belt
const FILL_FRACTION = 0.45; // start scrolling once the belt fills this far across
const FILL_THRESHOLD = HOUSING_RIGHT + FILL_FRACTION * (BELT_RIGHT - HOUSING_RIGHT);
const OPTIM_BLOCKS = 3; // uniform short optimizer step after the optim barrier, drawn spanning all lanes
const TRAIN_DIVISOR = 5; // training blocks per lane = ceil(blocks it generated / this)
const TRAIN_MIN = 3; // floor on training blocks per lane, so the train phase always reads
const ROLLOUT_MIN = 3; // rollout token count (excluding the prompt) is uniform in [MIN, MAX] — short, so one
const ROLLOUT_MAX = 5; // phase doesn't blanket the whole belt; varied so lanes straggle
const rolloutLen = () => ROLLOUT_MIN + Math.floor(Math.random() * (ROLLOUT_MAX - ROLLOUT_MIN + 1));

// fixed framing: the zoom never changes (1–4 lanes all fit at this size). the view is panned left so
// the left-anchored housing + GPU labels always clear the frame edge, and panned in Z to follow the
// stack as it expands downward from GPU 0. zooming out to fit instead would shrink the lanes.
const CAM_SIZE = 5; // orthographic half-height; frames up to MAX_LANES with margin
const CAM_PAN_X = -2.5; // shift the look-at left so the housing column clears the frame edge
const CAM_FOLLOW = 6; // Z-pan follow rate (per second); orbit doesn't smooth pan, so we lerp it here

// GPU 0 is anchored at z = 0; additional lanes expand downward (+Z). the camera pans in Z to follow.
const laneZ = (i: number) => i * LANE_WIDTH;
// rail i (0..n) is the shared boundary between lanes i-1 and i; lane i spans railZ(i)..railZ(i+1)
const railZ = (i: number) => (i - 0.5) * LANE_WIDTH;
// center of an n-lane stack anchored at z = 0 — the camera's Z look-at target
const stackCenter = (n: number) => ((n - 1) / 2) * LANE_WIDTH;

// a unit of work on the belt. u is its fixed belt-coordinate (its "local time"); world X is derived every
// frame as u - scroll (the belt's "global" clock). lane sets Z. rollout groups the blocks of one run so the
// final version can break runs apart on drain. born is the wall-clock emit time; (ex, ey, ez) is the
// entrance offset from the belt position at spawn, decaying to zero over the entrance — a vertical drop for
// tokens, a vector from the pool for the prompt.
const Block = {
    u: sparse(f32),
    lane: sparse(f32),
    rollout: sparse(f32),
    kind: sparse(f32),
    born: sparse(f32),
    ex: sparse(f32),
    ey: sparse(f32),
    ez: sparse(f32),
    span: sparse(f32), // 0 = a normal per-lane block; n > 0 = one block spanning n lanes (the unified optim step)
};

// a prompt waiting in the pool. q is its slot in the FIFO queue (0 = front, nearest the belt). its world
// position eases toward poolSlotZ(q) every frame; on pull, the entity sheds Pool and gains Block.
const Pool = {
    q: sparse(f32),
};

// a static colored box: cube mesh scaled to (sx, sy, sz), centered at (x, y, z)
function box(state: State, x: number, y: number, z: number, sx: number, sy: number, sz: number, rgb: [number, number, number]) {
    const eid = state.create();
    state.add(eid, Transform);
    state.add(eid, Part);
    state.add(eid, Color);
    Transform.scale.set(eid, sx, sy, sz, 0);
    Transform.pos.set(eid, x, y, z, 0);
    Color.rgba.set(eid, rgb[0], rgb[1], rgb[2], 1);
    return eid;
}

// a flat text label on the housing ceiling at (x, z). returns the eid so its content can be updated later.
function makeLabel(state: State, content: string, size: number, x: number, z: number) {
    const eid = state.create();
    state.add(eid, Transform);
    state.add(eid, Text);
    Text.content.set(eid, text(content));
    Text.fontSize.set(eid, size);
    Text.anchor.set(eid, 0.5, 0.5);
    Text.color.set(eid, LABEL_COLOR);
    Transform.pos.set(eid, x, LABEL_Y, z, 0);
    Transform.rot.set(eid, -0.7071, 0, 0, 0.7071); // lay the text flat (rot -90° about X)
    return eid;
}

// a flat text label that lives on the ground (not the housing ceiling), positioned and shown/hidden every
// frame. used for the barrier labels, which track their scrolling sync line. starts empty (hidden).
function makeGroundLabel(state: State, size: number, color: number) {
    const eid = state.create();
    state.add(eid, Transform);
    state.add(eid, Text);
    Text.content.set(eid, text(""));
    Text.fontSize.set(eid, size);
    Text.anchor.set(eid, 0.5, 0.5);
    Text.color.set(eid, color);
    Transform.rot.set(eid, -0.7071, 0, 0, 0.7071);
    return eid;
}

// retained housing + GPU-label entities, the per-lane state-status labels, and the lane count they were built
// for. rebuilt whole when the count changes (infrequent, so destroy-and-recreate is fine).
let furniture: number[] = [];
let stateLabels: number[] = [];
let stateShown: string[] = [];
let builtLanes = -1;

// the ground rails are immediate-mode, redrawn every frame, so they track lane count for free
const RailSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update() {
        const n = laneCount();
        for (let i = 0; i <= n; i++) {
            const z = railZ(i);
            segment([-HALF_LENGTH, GROUND_Y, z], [HALF_LENGTH, GROUND_Y, z], RAIL_COLOR, RAIL_THICKNESS);
        }
    },
};

// reconcile the housing gantry + per-lane labels to the lane count
const FurnitureSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update(state) {
        const n = laneCount();
        if (builtLanes === n) return;
        for (const eid of furniture) state.destroy(eid);
        for (const eid of stateLabels) state.destroy(eid);
        furniture = [];
        stateLabels = [];
        stateShown = [];
        for (let i = 0; i <= n; i++) {
            furniture.push(box(state, HOUSING_X, WALL_HEIGHT / 2, railZ(i), HOUSING_DEPTH, WALL_HEIGHT, WALL_THICKNESS, HOUSING_COLOR));
        }
        furniture.push(box(state, HOUSING_X, CEILING_Y, stackCenter(n), HOUSING_DEPTH, CEILING_THICKNESS, n * LANE_WIDTH, HOUSING_COLOR));
        for (let i = 0; i < n; i++) {
            furniture.push(makeLabel(state, `GPU ${i}`, GPU_LABEL_SIZE, HOUSING_X + GPU_LABEL_DX, railZ(i) + GPU_LABEL_DZ));
            stateLabels[i] = makeLabel(state, "", STATE_LABEL_SIZE, HOUSING_X + STATE_LABEL_DX, railZ(i + 1) + STATE_LABEL_DZ);
            stateShown[i] = "";
        }
        builtLanes = n;
    },
};

// fixed zoom + left pan, with the Z look-at lerped to follow the stack center as lanes are added
const cam = { panZ: 0 };

const CameraSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update(state) {
        const target = stackCenter(laneCount());
        cam.panZ += (target - cam.panZ) * Math.min(1, state.time.deltaTime * CAM_FOLLOW);
        for (const eid of state.query([Orbit])) {
            Orbit.size.set(eid, CAM_SIZE);
            Orbit.pan.set(eid, CAM_PAN_X, 0, cam.panZ, 0);
        }
    },
};

// one-shot intro: the camera starts a touch rotated and eases into its resting framing on load, so the
// hero reads as a manipulable 3D object without any overlay. slaves the smoothed yaw along an ease-out
// from offset to rest (controls the duration directly; the orbit's own damping settles too fast). a short
// hold sits at the offset before the ease, and any orbit drag cancels it so it never fights the reader.
const INTRO_DELAY = 0.3; // seconds held at the offset before easing in
const INTRO_DUR = 1.4; // ease-in duration
const INTRO_OFFSET = 0.3; // starting yaw offset in radians (~17°), eases to rest
const intro = { t: 0, rest: 0, done: false };

const IntroSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update(state) {
        if (intro.done) return;
        if (Inputs.mouse.left) {
            intro.done = true;
            return;
        }
        intro.t += state.time.deltaTime;
        const p = Math.max(0, Math.min(1, (intro.t - INTRO_DELAY) / INTRO_DUR));
        const ease = 1 - (1 - p) ** 3;
        for (const eid of state.query([Orbit])) {
            if (intro.rest === 0) intro.rest = Orbit.yaw.get(eid);
            const yaw = intro.rest + INTRO_OFFSET * (1 - ease);
            Orbit.yaw.set(eid, yaw);
            if (state.has(eid, OrbitSmooth)) OrbitSmooth.yaw.set(eid, yaw);
        }
        if (p >= 1) intro.done = true;
    },
};

// a spanning block (span = n lanes) reaches from the center of lane 0's footprint to lane n−1's, so it lines
// up with the per-lane blocks it replaces. span ≤ 1 collapses to a normal single-lane block.
const blockWidthZ = (span: number) => (span > 1 ? (span - 1) * LANE_WIDTH + BLOCK_WIDTH : BLOCK_WIDTH);
const blockCenterZ = (lane: number, span: number) => (span > 1 ? stackCenter(span) : laneZ(lane));

// spawn a fresh belt block that drops straight in from above: rollout tokens, training, and optimizer blocks.
// the prompt is the exception — it flies in from the pool via pullPrompt. span > 1 makes one block stretch
// across all lanes — the synchronized optimizer step, drawn as a single update rather than one per lane.
function spawnBlock(state: State, lane: number, u: number, rollout: number, kind: number, born: number, span = 0) {
    const eid = state.create();
    state.add(eid, Transform);
    state.add(eid, Part);
    state.add(eid, Color);
    state.add(eid, Block);
    Block.u.set(eid, u);
    Block.lane.set(eid, lane);
    Block.rollout.set(eid, rollout);
    Block.kind.set(eid, kind);
    Block.born.set(eid, born);
    Block.ex.set(eid, 0);
    Block.ey.set(eid, DROP_HEIGHT);
    Block.ez.set(eid, 0);
    Block.span.set(eid, span);
    Transform.scale.set(eid, BLOCK_LEN, BLOCK_HEIGHT, blockWidthZ(span), 0);
    Transform.pos.set(eid, u, BASE_Y + DROP_HEIGHT, blockCenterZ(lane, span), 0);
    const c = KIND_COLOR[kind];
    Color.rgba.set(eid, c[0], c[1], c[2], 1);
}

// pull a pooled prompt onto the belt: the entity sheds Pool and gains Block in place. its entrance offset is
// where it currently sits (in the pool) relative to its belt resting spot, so the belt entrance flies it the
// rest of the way — one continuous motion, no jump.
function pullPrompt(state: State, eid: number, lane: number, u: number, rollout: number, born: number) {
    const cx = Transform.pos.x.get(eid);
    const cy = Transform.pos.y.get(eid);
    const cz = Transform.pos.z.get(eid);
    state.remove(eid, Pool);
    state.add(eid, Block);
    Block.u.set(eid, u);
    Block.lane.set(eid, lane);
    Block.rollout.set(eid, rollout);
    Block.kind.set(eid, KIND.prompt);
    Block.born.set(eid, born);
    Block.ex.set(eid, cx - (u - loop.scroll));
    Block.ey.set(eid, cy - BASE_Y);
    Block.ez.set(eid, cz - laneZ(lane));
    Block.span.set(eid, 0);
}

// the "Prompts" label, left of the front slot — built once (it's not lane- or batch-dependent)
let poolLabel = -1;

function buildPoolLabel(state: State) {
    poolLabel = state.create();
    state.add(poolLabel, Transform);
    state.add(poolLabel, Text);
    Text.content.set(poolLabel, text("Prompts"));
    Text.fontSize.set(poolLabel, 0.4);
    Text.anchor.set(poolLabel, 0.5, 0.5);
    Text.color.set(poolLabel, LABEL_COLOR);
    Transform.pos.set(poolLabel, POOL_X - POOL_LABEL_DX, GROUND_Y + 0.02, POOL_FRONT_Z, 0);
    Transform.rot.set(poolLabel, -0.7071, 0, 0, 0.7071); // flat on the ground, like the GPU labels
}

// draw the queue as a chute: two black side rails running its length (along Z), padded off the cubes — like
// the belt lanes, so the queue reads even when empty. extends off-screen for large batch.
function drawPoolRails() {
    const batch = batchCount();
    if (batch <= 0) return;
    const halfW = BLOCK_LEN / 2 + POOL_RAIL_PAD;
    const frontZ = POOL_FRONT_Z + POOL_PITCH_Z / 2;
    const backZ = poolSlotZ(batch - 1) - POOL_PITCH_Z / 2;
    const y = GROUND_Y + 0.005;
    segment([POOL_X - halfW, y, frontZ], [POOL_X - halfW, y, backZ], RAIL_COLOR, RAIL_THICKNESS);
    segment([POOL_X + halfW, y, frontZ], [POOL_X + halfW, y, backZ], RAIL_COLOR, RAIL_THICKNESS);
}

// draw the queue rails (always, so the pool reads even when empty), then ease each pooled prompt toward its
// slot. the pool is filled at loop start (refillPool) and only depletes as lanes pull — it does not refill
// mid-loop; that depletion is the batch being consumed.
const PoolSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update(state) {
        if (poolLabel < 0) buildPoolLabel(state);
        drawPoolRails();
        const a = 1 - Math.exp(-POOL_EASE_RATE * state.time.deltaTime);
        for (const eid of state.query([Pool])) {
            const tz = poolSlotZ(Pool.q.get(eid));
            const cx = Transform.pos.x.get(eid);
            const cy = Transform.pos.y.get(eid);
            const cz = Transform.pos.z.get(eid);
            Transform.pos.set(eid, cx + (POOL_X - cx) * a, cy + (BASE_Y - cy) * a, cz + (tz - cz) * a, 0);
        }
    },
};

// fill the pool with `batch` prompts, each spawned just behind its slot so it eases in
function refillPool(state: State) {
    for (const eid of state.query([Pool])) state.destroy(eid);
    const batch = batchCount();
    for (let q = 0; q < batch; q++) {
        const eid = box(state, POOL_X, BASE_Y, poolSlotZ(q) - POOL_STAGGER, BLOCK_LEN, BLOCK_HEIGHT, BLOCK_WIDTH, KIND_COLOR[KIND.prompt]);
        state.add(eid, Pool);
        Pool.q.set(eid, q);
    }
}

// pull the front prompt (q = 0) onto `lane`'s belt at `u`, advancing the rest of the queue forward one slot.
// returns false when the pool is empty.
function tryPull(state: State, lane: number, u: number, rollout: number, born: number): boolean {
    let front = -1;
    let minQ = Infinity;
    for (const eid of state.query([Pool])) {
        const q = Pool.q.get(eid);
        if (q < minQ) {
            minQ = q;
            front = eid;
        }
    }
    if (front < 0) return false;
    pullPrompt(state, front, lane, u, rollout, born);
    for (const eid of state.query([Pool])) Pool.q.set(eid, Pool.q.get(eid) - 1);
    return true;
}

// per-lane state machines, indexed by lane. only systems touch these.
type LaneState = "gen" | "genDone" | "train" | "trainDone" | "optim" | "optimDone";

// the status shown in each lane's housing label. the two waiting states read as "idle".
const STATE_TEXT: Record<LaneState, string> = {
    gen: "rollout",
    genDone: "idle",
    train: "train",
    trainDone: "idle",
    optim: "optim",
    optimDone: "idle",
};

// the status label is colored to match the phase's block; the idle waits read in a muted grey.
const C_IDLE = 0x595959;
const STATE_COLOR: Record<LaneState, number> = {
    gen: C_ROLLOUT,
    genDone: C_IDLE,
    train: C_TRAIN,
    trainDone: C_IDLE,
    optim: C_OPTIM,
    optimDone: C_IDLE,
};

const lane = {
    state: [] as LaneState[],
    rolloutLeft: [] as number[], // rollout tokens left in the current rollout (the prompt is already emitted)
    genCount: [] as number[], // blocks generated this batch (prompt + tokens) — sets this lane's training length
    trainLeft: [] as number[],
    optimLeft: [] as number[],
    rollout: [] as number[], // current rollout id, for grouping a run's blocks
};

// the belt's shared clock and the global tick cursor. only systems touch this.
const loop = {
    moving: false, // false until the belt has filled past FILL_THRESHOLD
    scroll: 0, // belt translation (the global clock)
    emitTimer: 0,
    nextU: BELT_START_U, // belt-coord of the next tick's slot
    activeLanes: -1, // lane count the current loop was started for; mismatch forces a restart
    activeBatch: -1, // batch size the current loop was started for; mismatch forces a restart
    nextRollout: 0, // monotonic rollout id source
    trainBarrierU: NaN, // belt-coord where training began (the train barrier); NaN until it fires
    optimBarrierU: NaN, // belt-coord where the optimizer step began (the optim barrier)
};

// (re)start the loop: clear the belt, refill the pool, reset every lane to gen.
function startLoop(state: State) {
    for (const eid of state.query([Block])) state.destroy(eid);
    refillPool(state);
    const n = laneCount();
    for (let i = 0; i < n; i++) {
        lane.state[i] = "gen";
        lane.rolloutLeft[i] = 0; // 0 ⇒ needs a prompt; the next tick pulls one
        lane.genCount[i] = 0;
        lane.trainLeft[i] = 0;
        lane.optimLeft[i] = 0;
        lane.rollout[i] = 0;
    }
    loop.moving = false;
    loop.scroll = 0;
    loop.emitTimer = 0;
    loop.nextU = BELT_START_U;
    loop.activeLanes = n;
    loop.activeBatch = batchCount();
    loop.nextRollout = 0;
    loop.trainBarrierU = NaN;
    loop.optimBarrierU = NaN;
}

const allLanesIn = (n: number, ...states: LaneState[]) => {
    for (let i = 0; i < n; i++) if (!states.includes(lane.state[i])) return false;
    return true;
};

// one global tick: release the barriers if every lane has reached them, then emit one block per lane per its
// state (or nothing — a gap — while a lane waits at a barrier), and advance the belt cursor by one slot.
function tick(state: State, now: number, n: number) {
    if (allLanesIn(n, "genDone")) {
        loop.trainBarrierU = loop.nextU; // the sync boundary: every lane starts training at this slot
        for (let i = 0; i < n; i++) {
            lane.state[i] = "train";
            lane.trainLeft[i] = Math.max(TRAIN_MIN, Math.ceil(lane.genCount[i] / TRAIN_DIVISOR));
        }
    } else if (allLanesIn(n, "trainDone")) {
        loop.optimBarrierU = loop.nextU;
        for (let i = 0; i < n; i++) {
            lane.state[i] = "optim";
            lane.optimLeft[i] = OPTIM_BLOCKS;
        }
    }

    const u = loop.nextU;
    for (let i = 0; i < n; i++) {
        switch (lane.state[i]) {
            case "gen":
                if (lane.rolloutLeft[i] > 0) {
                    spawnBlock(state, i, u, lane.rollout[i], KIND.rollout, now);
                    lane.rolloutLeft[i]--;
                    lane.genCount[i]++;
                } else if (tryPull(state, i, u, loop.nextRollout, now)) {
                    lane.rollout[i] = loop.nextRollout++;
                    lane.rolloutLeft[i] = rolloutLen();
                    lane.genCount[i]++; // the prompt counts toward the generated total
                } else {
                    lane.state[i] = "genDone"; // pool empty: this lane is done generating
                }
                break;
            case "train":
                if (lane.trainLeft[i] > 0) {
                    spawnBlock(state, i, u, lane.rollout[i], KIND.train, now);
                    lane.trainLeft[i]--;
                } else lane.state[i] = "trainDone";
                break;
            case "optim":
                if (lane.optimLeft[i] > 0) {
                    // every lane enters optim on the same tick and runs the same count, so lane 0 emits one
                    // block spanning all lanes — the all-reduced, synchronized step — and the rest just count down
                    if (i === 0) spawnBlock(state, 0, u, lane.rollout[i], KIND.optimize, now, n);
                    lane.optimLeft[i]--;
                } else lane.state[i] = "optimDone";
                break;
            // genDone / trainDone / optimDone: idle, emit nothing
        }
    }
    loop.nextU += PITCH;
}

// the heart of the demo: start/restart the loop, advance the belt, tick every lane's state machine on cadence,
// and retire blocks that scrolled under the housing. the only system that creates or destroys a Block.
const LoopSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update(state) {
        // first run, or lanes/batch changed: restart so the new config takes effect immediately
        if (loop.activeLanes !== laneCount() || loop.activeBatch !== batchCount()) startLoop(state);
        const n = laneCount();
        const dt = state.time.deltaTime;
        const now = state.time.elapsed;

        if (loop.moving) loop.scroll += SPEED * dt;

        loop.emitTimer += dt;
        while (loop.emitTimer >= EMIT_INTERVAL) {
            loop.emitTimer -= EMIT_INTERVAL;
            tick(state, now, n);
            if (!loop.moving && loop.nextU - PITCH - loop.scroll >= FILL_THRESHOLD) loop.moving = true;
        }

        // retire blocks that scrolled fully under the housing, or whose lane no longer exists
        const dead: number[] = [];
        let alive = 0;
        for (const eid of state.query([Block])) {
            if (Block.lane.get(eid) >= n) {
                dead.push(eid);
                continue;
            }
            const rightEdge = Block.u.get(eid) - loop.scroll + BLOCK_LEN / 2;
            if (rightEdge < HOUSING_RIGHT) dead.push(eid);
            else alive++;
        }
        for (const eid of dead) state.destroy(eid);

        // every lane done and the belt drained: restart
        if (allLanesIn(n, "optimDone") && alive === 0) startLoop(state);
    },
};

// the only system that positions a block: the belt resting position (X from belt-coord minus scroll, Z from
// lane) plus the spawn offset eased to zero, so the block glides straight to its resting spot. easeOutExpo
// leaves `frac` of the offset at normalized age k — snappy approach, no overshoot or last-moment shift.
const PositionSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    after: [LoopSystem],
    update(state) {
        const now = state.time.elapsed;
        for (const eid of state.query([Block])) {
            const dur = Block.kind.get(eid) === KIND.prompt ? FLIGHT_DURATION : DROP_DURATION;
            const k = (now - Block.born.get(eid)) / dur;
            const frac = k >= 1 ? 0 : 2 ** (-10 * k); // remaining offset under easeOutExpo
            const x = Block.u.get(eid) - loop.scroll + frac * Block.ex.get(eid);
            const y = BASE_Y + frac * Block.ey.get(eid);
            const z = blockCenterZ(Block.lane.get(eid), Block.span.get(eid)) + frac * Block.ez.get(eid);
            Transform.pos.set(eid, x, y, z, 0);
        }
    },
};

// update each lane's housing status label when its state changes (rollout / train / optim / idle)
const StatusSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    after: [LoopSystem, FurnitureSystem],
    update() {
        const n = laneCount();
        for (let i = 0; i < n; i++) {
            const label = stateLabels[i];
            if (label === undefined) continue;
            const st = lane.state[i];
            const s = STATE_TEXT[st] ?? "";
            if (stateShown[i] !== s) {
                Text.content.set(label, text(s));
                Text.color.set(label, STATE_COLOR[st]);
                stateShown[i] = s;
            }
        }
    },
};

// persistent "train"/"optim" text entities that ride below their sync line, colored to match the phase.
// created lazily on first update; positioned and shown/hidden every frame to track the scrolling barrier.
let trainLabelE = -1;
let optimLabelE = -1;

// watch the collective state and draw a vertical line across all lanes at each barrier that has fired, with a
// "train"/"optim" label below it. the barriers are recorded in belt-coords (loop.trainBarrierU / optimBarrierU)
// so they scroll with the belt and vanish under the housing; cleared on restart.
const BarrierSystem: System = {
    group: "simulation",
    annotations: { mode: "always" },
    update(state) {
        const n = laneCount();
        const z0 = railZ(0);
        const z1 = railZ(n);
        const y = GROUND_Y + 0.006;
        if (trainLabelE < 0) trainLabelE = makeGroundLabel(state, BARRIER_LABEL_SIZE, C_TRAIN);
        if (optimLabelE < 0) optimLabelE = makeGroundLabel(state, BARRIER_LABEL_SIZE, C_OPTIM);
        const labelZ = z1 + BARRIER_LABEL_DZ; // below the stack's far rail, tracking the line in X
        // draw a barrier line + position/show its label, or hide the label if the barrier hasn't fired or has
        // scrolled under the housing
        const place = (eid: number, bu: number, content: string) => {
            const x = bu - PITCH / 2 - loop.scroll; // sit in the gap just before the barrier's first column
            const show = Number.isFinite(bu) && x >= HOUSING_RIGHT;
            Text.content.set(eid, text(show ? content : ""));
            if (show) {
                segment([x, y, z0], [x, y, z1], RAIL_COLOR, BARRIER_THICK);
                Transform.pos.set(eid, x, BARRIER_LABEL_Y, labelZ, 0);
            }
        };
        place(trainLabelE, loop.trainBarrierU, "train");
        place(optimLabelE, loop.optimBarrierU, "optim");
    },
};

const DemoPlugin: Plugin = {
    name: "HarmonyDemo",
    systems: [RailSystem, FurnitureSystem, PoolSystem, CameraSystem, IntroSystem, LoopSystem, PositionSystem, StatusSystem, BarrierSystem],
    components: { Block, Pool },
    dependencies: [LinesPlugin, TextPlugin, PartPlugin],
    initialize() {
        loop.activeLanes = -1; // force LoopSystem to start a fresh loop on its first run
        intro.t = 0;
        intro.rest = 0;
        intro.done = false;
        furniture = [];
        stateLabels = [];
        stateShown = [];
        builtLanes = -1;
        poolLabel = -1;
        trainLabelE = -1;
        optimLabelE = -1;
        cam.panZ = stackCenter(laneCount());
    },
};

await start([DemoPlugin, TextPlugin], "../scenes/loop.scene");

// console handle for live configuration, e.g. `__demo.lanes = 4`
(globalThis as Record<string, unknown>).__demo = config;

// the educational walkthrough (outer layer) drives the demo from the parent frame by posting config. it's
// the same surface as __demo — sets data, never sequencing — just cross-frame. unknown keys are ignored.
window.addEventListener("message", (e: MessageEvent) => {
    const d = e.data;
    if (!d || typeof d !== "object" || d.source !== "harmony-walkthrough") return;
    if (typeof d.lanes === "number") config.lanes = d.lanes;
    if (typeof d.batch === "number") config.batch = d.batch;
});
